import React, { useState } from 'react';
import { Clock, Mail, MapPin, MessageCircle, Phone } from 'lucide-react';
import SEO from '../components/SEO';
import { SitePage, PageHero } from '../components/site/ui';
import { BRAND, waLink } from '../components/site/brand';
import { submitEnquiry } from '../components/site/api';
import HoneyPot from '../components/site/HoneyPot';

const EMPTY = { name: '', phone: '', email: '', destination: '', date: '', travelers: '', message: '' };

const ContactPage = () => {
  const [f, setF] = useState(EMPTY);
  const [state, setState] = useState({ busy: false, ok: false, err: '' });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, ok: false, err: '' });
    try {
      await submitEnquiry({
        name: f.name.trim(), phone: f.phone.trim(), email: f.email.trim(),
        destination: f.destination.trim(), travelDate: f.date, travelers: f.travelers, message: f.message,
      });
      setState({ busy: false, ok: true, err: '' });
      setF(EMPTY);
    } catch {
      setState({ busy: false, ok: false, err: `Sorry, that didn't go through. Please WhatsApp or call ${BRAND.phone}.` });
    }
  };

  return (
    <SitePage>
      <div data-testid="contact-us-page">
        <SEO title="Contact VK Tours" description={`Call ${BRAND.phone}, WhatsApp or email ${BRAND.email}. Head office in Vijayawada; offices in Delhi NCR, USA and Maldives.`} path="/contact-us" />
        <PageHero
          short
          crumbs={[{ label: 'Home', to: '/' }, { label: 'Contact' }]}
          kicker="We reply within working hours"
          title="Let’s plan"
          accent="your trip"
          lede="Call, WhatsApp or send the form — tell us where you want to go and we’ll come back with a clear quote."
        >
          <a className="vk-btn vk-btn--whatsapp" href={waLink()} target="_blank" rel="noopener noreferrer"><MessageCircle size={18} /> WhatsApp us</a>
          <a className="vk-btn vk-btn--ghost" href={BRAND.phoneLink}><Phone size={18} /> {BRAND.phone}</a>
        </PageHero>

        <section className="vk-section">
          <div className="vk-wrap vk-split vk-split--form">
            <div className="vk-panel" style={{ padding: 28 }}>
              <p className="vk-kicker">Enquiry form</p>
              <h2 className="vk-display" style={{ fontSize: 'clamp(30px,4vw,44px)', margin: '0 0 20px' }}>Tell us about your trip</h2>
              {state.ok ? (
                <div className="vk-alert vk-alert--ok" role="status">Thank you — your enquiry has reached our team. We'll be in touch shortly.</div>
              ) : (
                <form className="vk-form" onSubmit={submit}><HoneyPot />
                  <div className="vk-form__row">
                    <div className="vk-field"><label htmlFor="c-name">Full name *</label><input id="c-name" required value={f.name} onChange={set('name')} autoComplete="name" /></div>
                    <div className="vk-field"><label htmlFor="c-phone">Phone *</label><input id="c-phone" required type="tel" value={f.phone} onChange={set('phone')} autoComplete="tel" /></div>
                  </div>
                  <div className="vk-form__row">
                    <div className="vk-field"><label htmlFor="c-email">Email *</label><input id="c-email" required type="email" value={f.email} onChange={set('email')} autoComplete="email" /></div>
                    <div className="vk-field"><label htmlFor="c-dest">Destination</label><input id="c-dest" value={f.destination} onChange={set('destination')} placeholder="e.g. Bali, Kashmir, Europe" /></div>
                  </div>
                  <div className="vk-form__row">
                    <div className="vk-field"><label htmlFor="c-date">Travel date</label><input id="c-date" type="date" min={new Date().toISOString().split('T')[0]} value={f.date} onChange={set('date')} /></div>
                    <div className="vk-field"><label htmlFor="c-trav">Travellers</label><input id="c-trav" type="number" min="1" max="200" value={f.travelers} onChange={set('travelers')} /></div>
                  </div>
                  <div className="vk-field"><label htmlFor="c-msg">Message</label><textarea id="c-msg" rows={4} value={f.message} onChange={set('message')} placeholder="Budget, hotel category, flights from your city…" /></div>
                  {state.err ? <div className="vk-alert vk-alert--err" role="alert">{state.err}</div> : null}
                  <button type="submit" className="vk-btn vk-btn--hot" disabled={state.busy}>{state.busy ? 'Sending…' : 'Send enquiry'}</button>
                </form>
              )}
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div className="vk-panel vk-panel--sand">
                <h3>Talk to us</h3>
                <ul className="vk-ticks">
                  <li><Phone size={18} color="var(--hot)" /> <span><a href={BRAND.phoneLink}>{BRAND.phone}</a> (mobile & WhatsApp)</span></li>
                  <li><Phone size={18} color="var(--hot)" /> <span><a href={BRAND.landlineLink}>{BRAND.landline}</a> (office)</span></li>
                  <li><Mail size={18} color="var(--hot)" /> <a href={`mailto:${BRAND.email}`}>{BRAND.email}</a></li>
                  <li><Clock size={18} color="var(--hot)" /> <span>Mon–Sat 10:00 AM – 7:00 PM IST · Sunday by appointment</span></li>
                </ul>
              </div>
              <div className="vk-panel">
                <h3>Head office</h3>
                <p style={{ display: 'flex', gap: 10, margin: 0, lineHeight: 1.6 }}><MapPin size={18} color="var(--hot)" style={{ flex: 'none', marginTop: 3 }} /> {BRAND.address}</p>
                <p className="vk-cta__offices" style={{ marginTop: 18, fontSize: 13 }}>{BRAND.offices}</p>
              </div>
              <div className="vk-panel">
                <h3>Complaints & refunds</h3>
                <p style={{ margin: 0, color: 'var(--muted)', lineHeight: 1.6 }}>Email <a href={`mailto:${BRAND.email}`} style={{ color: 'var(--hot-dark)', fontWeight: 700 }}>{BRAND.email}</a> with the subject “GRIEVANCE — [Booking ID]”. We acknowledge within 24 hours and aim to resolve within 7 working days.</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </SitePage>
  );
};

export default ContactPage;
